import { BaseType } from '../types/Enums';
import type { Building, BaseComponent, LevelModel, RoomModel } from '../types/protocols';
import type { Unit } from '../types/units';
import { translate } from '../types/EnumTranslations';
import { unitDisplayName, unitRoomKeys } from '../Helpers/unitUtils';
import type { SelectedImage, YearGroup, SubSection } from './useBuildingImages';
import { photoLocationList } from '../Helpers/photoLocations';

/**
 * Resolves a component's display name. Injected by the caller so this module
 * stays free of the app's heavy (Firebase-pulling) implementation imports and
 * remains unit-testable in isolation.
 */
export type ResolveDisplayName = (component: BaseComponent, building: Building) => string;

/**
 * Resolves the label for a reference photo from the parent component that owns it
 * (`photoLocations/{photoKey}.parentKey`). Injected by the caller so the title logic (and its
 * Firebase-pulling display-name resolution) stays out of this module.
 */
export type ResolvePhotoLabel = (parentKey: string | undefined) => string;

/**
 * A single window pinned onto a reference photo. Each marker carries its OWN
 * photo dimensions (from that pin's `PhotoLocation`) so it can be scaled
 * independently — we never assume two windows on the same photo recorded
 * identical natural dimensions.
 */
export interface WindowMarker {
    componentKey: string;
    x: number;
    y: number;
    width: number;
    height: number;
    displayName: string;
    /** Reused per-window subsection (year-grouped photos), rendered verbatim in Tier 3. */
    subSection: SubSection;
}

/**
 * A reference photo (a distinct `storagePath`) with every window marker pinned
 * to it. A window marked in several photos appears in each of their groups,
 * with one marker per group.
 */
export interface ReferencePhotoGroup {
    /** Stable identity — the photo's storage path. */
    key: string;
    storagePath: string;
    /** Display label: the name of the parent component the photo belongs to. */
    label: string;
    /** Parent component the photo belongs to (`PhotoLocation.parentKey`). */
    parentKey?: string;
    markers: WindowMarker[];
    /** Total window photos across all markers (for the group header). */
    totalCount: number;
}

/** Group a flat list of images into year buckets, newest year first, newest photo first. */
export const groupImagesByYear = (images: SelectedImage[]): YearGroup[] => {
    const imagesByYearMap = new Map<string, SelectedImage[]>();
    for (const image of images) {
        const year = new Date(image.photo.date).getFullYear().toString();
        if (!imagesByYearMap.has(year)) {
            imagesByYearMap.set(year, []);
        }
        imagesByYearMap.get(year)!.push(image);
    }

    return Array.from(imagesByYearMap.entries())
        .sort(([a], [b]) => parseInt(b) - parseInt(a))
        .map(([year, yearImages]) => ({
            year,
            images: yearImages.sort((a, b) => {
                const dateA = new Date(a.photo.date);
                const dateB = new Date(b.photo.date);
                return dateB.getTime() - dateA.getTime();
            })
        }));
};

// Build the per-window subsection (display name + year-grouped photos) reused in Tier 3.
const buildWindowSubSection = (
    componentKey: string,
    component: BaseComponent,
    building: Building,
    resolveDisplayName: ResolveDisplayName
): { subSection: SubSection; displayName: string } => {
    const displayName = resolveDisplayName(component, building);

    const componentImages: SelectedImage[] = Object.values(component.photos || {}).map(photo => ({
        type: 'component' as const,
        photo,
        componentKey,
        baseType: BaseType.Window,
        displayName
    }));

    return {
        displayName,
        subSection: {
            key: `component-${componentKey}`,
            label: displayName,
            componentKey,
            roomKey: null,
            imagesByYear: groupImagesByYear(componentImages),
            totalCount: componentImages.length
        }
    };
};

/**
 * Derive reference-photo groups and the unmarked-window fallback from the
 * building's window components.
 *
 * - A window with pins becomes a marker on every reference photo it is marked in
 *   (`photoLocations/{photoKey}.storagePath`) — included even when it has no
 *   photos yet, so it is reachable for upload (Tier 3 shows an empty state).
 * - A window without pins but with photos goes into `unmarked`
 *   (the "Omarkerade fönster" group), matching the old flat list's behavior of
 *   only surfacing components that have photos.
 */
export const buildReferencePhotoGroups = (
    windowComponents: [string, BaseComponent][],
    building: Building,
    resolveDisplayName: ResolveDisplayName,
    resolvePhotoLabel: ResolvePhotoLabel
): { groups: ReferencePhotoGroup[]; unmarked: SubSection[] } => {
    const groupMap = new Map<string, WindowMarker[]>();
    const parentKeyMap = new Map<string, string | undefined>();
    const unmarked: SubSection[] = [];

    for (const [componentKey, component] of windowComponents) {
        const pins = photoLocationList(component, building);
        const hasPhotos = !!component.photos && Object.keys(component.photos).length > 0;

        if (pins.length > 0) {
            const { subSection, displayName } = buildWindowSubSection(componentKey, component, building, resolveDisplayName);
            for (const { location: loc } of pins) {
                const marker: WindowMarker = {
                    componentKey,
                    x: loc.x,
                    y: loc.y,
                    width: loc.width,
                    height: loc.height,
                    displayName,
                    subSection
                };
                if (!groupMap.has(loc.storagePath)) {
                    groupMap.set(loc.storagePath, []);
                    parentKeyMap.set(loc.storagePath, loc.parentKey);
                }
                groupMap.get(loc.storagePath)!.push(marker);
            }
        } else if (hasPhotos) {
            unmarked.push(buildWindowSubSection(componentKey, component, building, resolveDisplayName).subSection);
        }
        // Window with neither pins nor photos: nothing to show (parity with old flat list).
    }

    const groups: ReferencePhotoGroup[] = Array.from(groupMap.entries()).map(([storagePath, markers]) => {
        const parentKey = parentKeyMap.get(storagePath);
        return {
            key: storagePath,
            storagePath,
            parentKey,
            label: resolvePhotoLabel(parentKey),
            markers: markers.sort((a, b) => a.displayName.localeCompare(b.displayName, 'sv')),
            totalCount: markers.reduce((sum, m) => sum + m.subSection.totalCount, 0),
        };
    });

    // Most-populated reference photos first; storagePath as a stable tiebreak.
    groups.sort((a, b) => b.markers.length - a.markers.length || a.storagePath.localeCompare(b.storagePath));

    unmarked.sort((a, b) => a.label.localeCompare(b.label, 'sv'));

    return { groups, unmarked };
};

/**
 * Where a room-bound component sits, shown under its name so a "Vägg 3" in the
 * list can be told from every other one: the room's level, the room and, when
 * the room belongs to one, the unit.
 */
export interface ComponentPlacement {
    levelName: string;
    roomName: string;
    unitName?: string;
}

/** `level.name`, else "Plan N"; empty when the level is gone. */
export const levelName = (levelKey: string | undefined, levels: { [key: string]: LevelModel } | undefined): string => {
    const level = levelKey ? levels?.[levelKey] : undefined;
    return level ? level.name || `Plan ${level.number}` : '';
};

/** "Kök 101": the room type and number, which is how this page names rooms. */
export const roomName = (room: Pick<RoomModel, 'type' | 'number'> | undefined): string => {
    const type = room?.type ? translate(room.type) : 'Rum';
    return room?.number ? `${type} ${room.number}` : type;
};

/**
 * The room a component is in: its own `roomKey` (walls, ceilings, floors; the
 * legacy plain string as well as the ValueWithMetadata) or `parentRoomKeys[0]`
 * (utilities, fixed furnishing, membranes, övrigt). Null when it has none.
 */
export const componentRoomKey = (component: BaseComponent): string | null => {
    const own = (component as { roomKey?: string | { value?: string | null } | null }).roomKey;
    if (typeof own === 'string' && own) return own;
    if (own && typeof own === 'object' && own.value) return own.value;
    return component.parentRoomKeys?.value?.[0] ?? null;
};

/** Room key → the name of the unit holding it (a room is in at most one unit). */
export const unitNameByRoom = (units: { [unitKey: string]: Unit } | undefined): Map<string, string> => {
    const map = new Map<string, string>();
    for (const unit of Object.values(units || {})) {
        const name = unitDisplayName(unit);
        for (const roomKey of unitRoomKeys(unit)) map.set(roomKey, name);
    }
    return map;
};

/** The component's placement, or null when it is not in an existing room. */
export const componentPlacement = (
    component: BaseComponent,
    structure: { rooms?: { [key: string]: RoomModel }; levels?: { [key: string]: LevelModel } } | undefined,
    unitNames: Map<string, string>
): ComponentPlacement | null => {
    const roomKey = componentRoomKey(component);
    const room = roomKey ? structure?.rooms?.[roomKey] : undefined;
    if (!roomKey || !room) return null;
    return {
        levelName: levelName(room.levelKey, structure?.levels),
        roomName: roomName(room),
        unitName: unitNames.get(roomKey),
    };
};

/** "Plan 2 · Kök 101 · Lägenhet 1203" — the parts that exist, in that order. */
export const placementLabel = (placement: ComponentPlacement): string =>
    [placement.levelName, placement.roomName, placement.unitName].filter(Boolean).join(' · ');

/**
 * A lock's section label: "Lås – Entrédörr", its own name followed by the door or
 * window it sits on (`parentKey`), so several locks of one type can be told apart.
 * Only the own name when the parent is gone.
 */
export const lockLabel = (
    lock: BaseComponent,
    building: Building,
    resolveName: ResolveDisplayName
): string => {
    const name = resolveName(lock, building);
    const parentKey = lock.parentKey?.value;
    const parent = parentKey ? building.model.structure?.components?.[parentKey] : undefined;
    return parent ? `${name} – ${resolveName(parent, building)}` : name;
};
